import { c as _c } from "react/compiler-runtime";
import type { EChartsOption, EChartsType } from 'echarts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { filledPlotHeight, onPrintLayoutChange, printablePlotHeight, printablePlotWidth, rememberPrintedSheetWidth, sheetBoxOf, sheetRoomUnder } from '@/lib/print/print-session';
import { FIGURE_CHROME, PRINT_FURNITURE } from '../chart-geometry';
import { fitDrawingToBox } from '../echarts/fit-drawing';
import { isSameTokens, readTokens, type ChartTokens } from '../chart-tokens';
interface PrintableChart {
  onChartReady: (chart: EChartsType) => void;
  /** The height the plot may have, in the state the page is in. */
  plotHeight: number;
  tokens: ChartTokens;
}

/** Turns of the measure-and-move the figure is settled on paper in. */
const SHEET_PASSES = 4;

/**
 * The foot of the figure this plot is drawn in — the foot of its last part
 * rather than of its frame. The frame is a flex item stretched to the row it
 * sits in, whether what it holds fills it or not; growing the plot moves what
 * it holds, and only then the frame. See `ChartFigure`.
 */
const figureFoot = (dom: HTMLElement): Element => {
  const figure = dom.closest('[data-slot="chart-figure"]') ?? dom;
  return figure.lastElementChild ?? figure;
};

/**
 * A chart that follows the theme, and survives being printed.
 *
 * Both the class next-themes writes and the system preference are watched: the
 * second matters while the theme is still "system", when switching the
 * operating system to dark changes no class on the page.
 *
 * Printing is the hard one. Everything else on the page is laid out again for
 * the sheet, but a figure holds its own size: it keeps the colours of the
 * screen, the width of the screen and the height it was given there. So it is
 * sized twice, once at each turn of a print.
 *
 * First when the browser announces the print, from the box the last print
 * was laid out in, or from the sheet the stylesheet asked for where there has
 * been no print — nothing is laid out for paper yet, so there is nothing to
 * measure. Then when the page is on paper, from the box the browser really
 * laid out: the margins and the paper of the print dialog are the reader's,
 * not the stylesheet's, and a figure sized for the wrong sheet leaves a band
 * of white beside it — or runs off the edge.
 *
 * Each turn is one synchronous flush, because the browser does not wait
 * between it and the printed rendering, and a second turn would never come.
 * Some browsers do not even leave the second turn: asked to fit a sheet the
 * page was not laid out for, they lay it out again, wider, after the last
 * turn of script. So the drawing is also handed its own shape, which lets the
 * print stylesheet stretch it to whatever box it ends up in, measured or not
 * — see `fitDrawingToBox`.
 */
export const usePrintableChart = (variant, screenPlotHeight, option, onReady) => {
  const $ = _c(14);
  const chart = useRef(null);
  const boxWatch = useRef(null);
  const isOnPaper = useRef(false);
  let t0;
  if ($[0] !== variant) {
    t0 = () => ({
      isPrinting: false,
      printedPlotHeight: printablePlotHeight(PRINT_FURNITURE[variant]),
      tokens: readTokens()
    });
    $[0] = variant;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [state, setState] = useState(t0);
  let t1;
  let t2;
  if ($[2] !== option || $[3] !== variant) {
    t1 = () => {
      const followTheme = () => setState(_temp);
      followTheme();
      const observer = new MutationObserver(followTheme);
      observer.observe(document.documentElement, {
        attributeFilter: ["class"],
        attributes: true
      });
      const scheme = window.matchMedia("(prefers-color-scheme: dark)");
      scheme.addEventListener("change", followTheme);
      const settle = (isPrinting, width, printedPlotHeight) => {
        isOnPaper.current = isPrinting;
        flushSync(() => setState(current_0 => ({
          isPrinting,
          printedPlotHeight: printedPlotHeight ?? current_0.printedPlotHeight,
          tokens: readTokens()
        })));
        if (option.current) {
          chart.current?.setOption(option.current, true);
        }
        chart.current?.resize({
          width
        });
        chart.current?.getZr().flush();
        const drawn = chart.current?.getDom();
        if (drawn) {
          fitDrawingToBox(drawn, isPrinting);
        }
      };
      const stopWatchingPrint = onPrintLayoutChange(phase => {
        const dom = chart.current?.getDom();
        if (phase === "on-screen") {
          settle(false, "auto");
          return;
        }
        if (phase === "about-to-print" || !dom) {
          settle(true, printablePlotWidth(FIGURE_CHROME[variant]), printablePlotHeight(PRINT_FURNITURE[variant]));
          return;
        }
        rememberPrintedSheetWidth();
        for (let pass = 0; pass < SHEET_PASSES; pass = pass + 1, pass) {
          const box = sheetBoxOf(dom);
          const height = filledPlotHeight(box.height, sheetRoomUnder(figureFoot(dom)));
          if (pass > 0 && Math.abs(height - box.height) < 1) {
            return;
          }
          settle(true, box.width, height);
        }
      });
      return () => {
        observer.disconnect();
        scheme.removeEventListener("change", followTheme);
        stopWatchingPrint();
      };
    };
    t2 = [option, variant];
    $[2] = option;
    $[3] = variant;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      boxWatch.current?.disconnect();
    };
    t4 = [];
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== onReady) {
    t5 = instance => {
      chart.current = instance;
      const box_0 = instance.getDom();
      boxWatch.current?.disconnect();
      boxWatch.current = new ResizeObserver(() => {
        if (!isOnPaper.current && !instance.isDisposed() && Math.abs(instance.getWidth() - box_0.clientWidth) >= 1) {
          instance.resize();
        }
      });
      boxWatch.current.observe(box_0);
      onReady?.(instance);
    };
    $[8] = onReady;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const onChartReady = t5;
  const t6 = state.isPrinting ? state.printedPlotHeight : screenPlotHeight;
  let t7;
  if ($[10] !== onChartReady || $[11] !== state.tokens || $[12] !== t6) {
    t7 = {
      onChartReady,
      plotHeight: t6,
      tokens: state.tokens
    };
    $[10] = onChartReady;
    $[11] = state.tokens;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};
function _temp(current) {
  const tokens = readTokens();
  return isSameTokens(current.tokens, tokens) ? current : {
    ...current,
    tokens
  };
}